import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Event, Team } from '../utils/types';
import { apiGet } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import AwsAccountAssignment from './AwsAccountAssignment';

const AwsAccountManagement: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const { token, user } = useAuth();

  // Check if current user has organizer role
  const isOrganizer = user?.roles?.some(role => role.toUpperCase() === 'ORGANIZER') || false;

  useEffect(() => {
    if (eventId && token) {
      loadEvent();
      loadEventTeams();
    }
  }, [eventId, token]);

  const loadEvent = async () => {
    if (!eventId || !token) return;
    
    setLoading(true);
    setError('');
    
    try {
      const eventData = await apiGet<Event>(`/events/${eventId}`, token);
      setEvent(eventData);
    } catch (err) {
      setError('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

    const loadEventTeams = async () => {
    try {
      setLoading(true);
      const teamData = await apiGet<Team[]>(`/teams?event_id=${eventId}`, token || undefined);
      setTeams(teamData || []);
    } catch (error) {
      console.error('Error loading teams:', error);
      setError('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  // Statistics calculations
  const totalTeams = teams.length;
  const assignedTeams = teams.filter(team => team.aws_account_assignment).length;
  const unassignedTeams = totalTeams - assignedTeams;

  // Account distribution
  const accountDistribution: { [key: string]: number } = {};
  teams.forEach(team => {
    if (team.aws_account_assignment) {
      accountDistribution[team.aws_account_assignment] = (accountDistribution[team.aws_account_assignment] || 0) + 1;
    }
  });  if (loading) {
    return <div className="loading">Loading event...</div>;
  }

  if (error) {
    return (
      <div className="error">
        {error}
        <Link to="/dashboard" className="btn btn-secondary" style={{ marginLeft: '10px' }}>
          Back to Dashboard
        </Link>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <h3>Event not found</h3>
        <Link to="/dashboard" className="btn">Back to Dashboard</Link>
      </div>
    );
  }

  if (!isOrganizer) {
    return (
      <div className="card" style={{ textAlign: 'center' }}>
        <h3>Access Denied</h3>
        <p>This page is only accessible to event organizers.</p>
        <Link to={`/events/${eventId}`} className="btn">Back to Event</Link>
      </div>
    );
  }

  return (
    <div>
      {/* Navigation */}
      <div style={{ marginBottom: '20px' }}>
        <Link to="/dashboard" className="btn btn-secondary">← Back to Dashboard</Link>
        <Link 
          to={`/events/${eventId}`} 
          className="btn btn-secondary" 
          style={{ marginLeft: '10px' }}
        >
          View Event
        </Link>
      </div>

      {/* Header */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
          <div>
            <h1 style={{ margin: 0 }}>☁️ AWS Account Management</h1>
            <h2 style={{ margin: '5px 0 0 0', fontSize: '18px', color: '#666' }}>
              {event.name}
            </h2>
          </div>
          <span className={`badge badge-${event.state}`} style={{ fontSize: '14px' }}>
            {event.state.toUpperCase()}
          </span>
        </div>

        <p style={{ color: '#666', marginBottom: '0' }}>
          Manage AWS account assignments for hackathon teams. Each team will be assigned to one of the 5 available hackathon AWS accounts.
        </p>
      </div>

      {/* Statistics Overview */}
      <div className="card">
        <h3>📊 Assignment Overview</h3>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '20px',
          marginBottom: '30px'
        }}>
          <div style={{ 
            background: '#f8f9fa', 
            padding: '20px', 
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#007bff' }}>
              {teams.length}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>Total Teams</div>
          </div>
          
          <div style={{ 
            background: '#d4edda', 
            padding: '20px', 
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#28a745' }}>
              {assignedTeams}
            </div>
            <div style={{ fontSize: '14px', color: '#155724' }}>Assigned Teams</div>
          </div>
          
          <div style={{ 
            background: '#fff3cd', 
            padding: '20px', 
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#856404' }}>
              {unassignedTeams}
            </div>
            <div style={{ fontSize: '14px', color: '#856404' }}>Pending Assignment</div>
          </div>
          
          <div style={{ 
            background: '#e7f3ff', 
            padding: '20px', 
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#0066cc' }}>
              {Object.keys(accountDistribution).length}
            </div>
            <div style={{ fontSize: '14px', color: '#0066cc' }}>Accounts in Use</div>
          </div>
        </div>

        {/* Account Distribution */}
        {Object.keys(accountDistribution).length > 0 && (
          <div>
            <h4>Account Distribution</h4>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: '15px' 
            }}>
              {Object.entries(accountDistribution).map(([account, count]) => (
                <div 
                  key={account}
                  style={{
                    background: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    padding: '15px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span style={{ fontWeight: 'bold' }}>{account}</span>
                  <span style={{
                    background: '#007bff',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '15px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    {count} team{count !== 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Team List */}
      <div className="card">
        <h3>👥 Team Assignment Status</h3>
        
        {teamsLoading ? (
          <div>Loading teams...</div>
        ) : teams.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <h4>No teams registered yet</h4>
            <p>Teams will appear here once they register for this event.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '15px' }}>
            {teams.map((team) => (
              <div 
                key={team.team_id} 
                style={{
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '20px',
                  background: team.aws_account_assignment ? '#f8f9fa' : '#fff3cd'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 10px 0' }}>
                      <Link 
                        to={`/teams/${team.team_id}`}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        {team.name}
                      </Link>
                    </h4>
                    
                    <div style={{ marginBottom: '10px', fontSize: '14px', color: '#666' }}>
                      👤 {team.member_count || 0} member{(team.member_count || 0) !== 1 ? 's' : ''}
                    </div>
                    
                    {team.description && (
                      <p style={{ margin: '0 0 15px 0', color: '#666', fontSize: '14px' }}>
                        {team.description.length > 150 
                          ? `${team.description.substring(0, 150)}...` 
                          : team.description
                        }
                      </p>
                    )}
                  </div>
                  
                  <div style={{ marginLeft: '20px' }}>
                    {team.aws_account_assignment ? (
                      <div style={{
                        background: '#28a745',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        minWidth: '140px'
                      }}>
                        ☁️ {team.aws_account_assignment}
                      </div>
                    ) : (
                      <div style={{
                        background: '#ffc107',
                        color: '#212529',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        minWidth: '140px'
                      }}>
                        ⏳ Pending
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AWS Account Assignment Component */}
      {eventId && (
        <div className="card">
          <AwsAccountAssignment 
            eventId={eventId}
            teams={teams}
            onAssignmentComplete={() => {
              // Reload teams after assignment to show updated status
              loadEventTeams();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AwsAccountManagement;
